<template>
    <q-card class="row content-stretch fit asset-card no-shadow border-grey">
        <!-- Header -->
        <q-card-section
            class="col-12 content-center text-white"
            :class="
                phase
                    ? 'bg-' + ASSET_STATUS_TO_STYLE[phase.status]['color']
                    : 'bg-' + ASSET_STATUS_TO_STYLE['unknown']['color']
            "
        >
            <div class="text-h6 text-center">{{ asset.name }}</div>
            <div
                class="text-subtitle1 text-center asset-card__asset asset-card__truncate"
            >
                {{ displayName }}
            </div>
            <div
                class="text-subtitle2 text-center text-uppercase asset-card__status"
            >
                {{
                    phase
                        ? $t(ASSET_STATUS_TO_STYLE[phase.status]['label'])
                        : '-'
                }}
            </div>
        </q-card-section>
        <!-- Asset -->
        <q-card-section class="col-12 content-center">
            <q-banner
                v-if="disconnected"
                inline-actions
                class="text-medium-grey bg-light-yellow disconnection_banner"
            >
                <div class="row justify-between">
                    <span>
                        <q-icon
                            name="fas fa-wifi-exclamation"
                            class="q-mr-sm"
                            color="medium-grey"
                            size="1rem"
                        />
                        {{ $t('Gateway disconnected') }}
                    </span>
                    <span v-if="$q.screen.gt.md" class="text-right">
                        {{ getDisconnectedDuration }}
                    </span>
                </div>
                <template v-slot:action>
                    <q-icon
                        name="fas fa-circle-info"
                        color="medium-grey"
                        size="1rem"
                    >
                        <q-tooltip>
                            <div v-if="!$q.screen.gt.md">
                                {{ $t('Total duration') }}:
                                {{ getDisconnectedDuration }}
                            </div>
                            <div>
                                {{
                                    $t(
                                        'A data acquisition device sending telemetry for this line is disconnected, and live data may be unavailable. Please contact on-site support.'
                                    )
                                }}
                            </div>
                        </q-tooltip>
                    </q-icon>
                </template>
            </q-banner>
            <div
                v-if="showCardMessage"
                style="min-height: 145px"
                class="flex flex-center full-height text-grey"
                :class="{ 'q-pt-lg': disconnected }"
            >
                <div v-if="status === 'changeover'" class="text-center">
                    <q-icon name="fas fa-sync-alt" size="lg" />
                    <div class="q-pt-md">
                        {{ $t('Switch in progress') }}
                    </div>
                </div>
                <span v-else class="text-center">{{ cardMessage }}</span>
            </div>
            <div v-else class="asset-card__content">
                <q-skeleton v-if="showLoading" height="25px" class="q-my-sm" />
                <div v-else>
                    <span class="text-medium-grey asset-card__stats_text">{{
                        $t('Product count')
                    }}</span>
                    <span
                        class="text-uppercase asset-card__status asset-card__stats_value"
                        >{{
                            'productCount' in this.stats &&
                            this.stats['productCount'] !== null &&
                            this.status != 'stopped' &&
                            this.status != 'changeover'
                                ? formatNumber(
                                      Math.trunc(this.stats['productCount']),
                                      'integer'
                                  )
                                : '-'
                        }}</span
                    >
                </div>

                <hr class="border-grey" />
                <q-skeleton v-if="showLoading" height="25px" class="q-my-md" />
                <div v-else>
                    <span class="text-medium-grey asset-card__stats_text">{{
                        $t('Performance')
                    }}</span>
                    <span
                        class="text-uppercase asset-card__status asset-card__stats_value"
                        >{{
                            'performance' in this.stats &&
                            this.stats['performance'] !== null &&
                            this.status != 'stopped' &&
                            this.status != 'changeover'
                                ? formatNumber(
                                      parseFloat(
                                          this.stats['performance'] * 100
                                      ),
                                      'float'
                                  ) + '%'
                                : '-'
                        }}</span
                    >
                    <q-linear-progress
                        instant-feedback
                        :value="
                            'performance' in this.stats &&
                            this.stats['performance'] !== null &&
                            this.status != 'stopped' &&
                            this.status != 'changeover'
                                ? this.stats['performance']
                                : 0
                        "
                        size="lg"
                        :color="
                            this.oeeWidgetColor(this.stats['performance'] * 100)
                        "
                    />
                </div>

                <hr
                    class="border-grey"
                    :class="{
                        'asset-card__stats_separator':
                            !isLoadingStats && !isLoading
                    }"
                />
                <q-skeleton v-if="showLoading" height="25px" class="q-my-sm" />
                <div v-else>
                    <span class="text-medium-grey asset-card__stats_text">{{
                        $t('Availability')
                    }}</span>
                    <span
                        class="text-uppercase asset-card__status asset-card__stats_value"
                        >{{
                            'availability' in this.stats &&
                            this.stats['availability'] !== null &&
                            this.status != 'stopped' &&
                            this.status != 'changeover'
                                ? formatNumber(
                                      parseFloat(
                                          this.stats['availability'] * 100
                                      ),
                                      'float'
                                  ) + '%'
                                : '-'
                        }}</span
                    >
                    <q-linear-progress
                        instant-feedback
                        :value="
                            'availability' in this.stats &&
                            this.stats['availability'] !== null &&
                            this.status != 'stopped' &&
                            this.status != 'changeover'
                                ? this.stats['availability']
                                : 0
                        "
                        size="lg"
                        :color="
                            this.oeeWidgetColor(
                                this.stats['availability'] * 100
                            )
                        "
                    />
                </div>
            </div>
        </q-card-section>
    </q-card>
</template>

<script>
import { ASSET_STATUS_TO_STYLE } from '@models/assets.js';
import { mapGetters } from 'vuex';
import { getCssVar } from 'quasar';
import {
    noSensorConnectedMessage,
    noDataAvailableMessage
} from '@utils/getMessages.js';
import { useSharedStore } from '@stores/shared.js';
import { useProductsStore } from '@stores/products.js';
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useCausesStore } from '@stores/causes.js';

export default {
    name: 'AssetCard',
    props: {
        /**
         * ID of asset.
         */
        assetId: {
            type: [Number, String],
            required: true
        },
        assetData: {
            type: Object,
            default: () => ({})
        },
        statsPeriod: {
            type: String,
            default: 'currentProduct',
            required: false
        },
        isLoadingStats: {
            type: Boolean,
            default: false
        },
        isLoadingDeviceStatus: {
            type: Boolean,
            default: false
        }
    },
    watch: {
        causeId: {
            immediate: true,
            async handler(id) {
                if (id) {
                    let cause = this.getCause(id);

                    // if cause is not in store, fetch it from API
                    if (cause === null) {
                        this.isLoading = true;
                        try {
                            await this.loadCauseAction(id);
                            cause = this.getCause(id);
                        } catch (error) {
                            this.$notifyError(
                                this.$t(
                                    'Failed to load cause details. The cause could have been deleted.'
                                )
                            );
                            console.error(error);
                        } finally {
                            this.isLoading = false;
                        }
                    }
                    // update cause name to display
                    this.causeName = cause ? cause.name : null;
                } else {
                    // reset cause name if cause id is null
                    this.causeName = null;
                }
            }
        },
        productId: {
            immediate: true,
            async handler(id) {
                // check if current phase has a product name already
                // for phases received by SignalR, product name is included in the payload
                // hence, we don't need to fetch it from the API
                if (this.phase && this.phase.currentProductName) {
                    this.productName = this.phase.currentProductName;
                } else {
                    if (id) {
                        let product = this.getProduct(id);

                        // if product is not in store, fetch it from API
                        if (product === null) {
                            this.isLoading = true;
                            await this.loadProductAction(id)
                                .then(() => {
                                    // update product
                                    product = this.getProduct(id);
                                })
                                .catch(error => {
                                    this.$notifyError(
                                        this.$t(
                                            'Failed to load product details.'
                                        )
                                    );
                                    console.error(error);
                                })
                                .finally(() => {
                                    this.isLoading = false;
                                });
                        }

                        // update product name to display
                        this.productName = product ? product.name : null;
                    }
                }
            }
        }
    },
    data() {
        return {
            // models
            ASSET_STATUS_TO_STYLE,
            isLoading: false,
            causeName: null,
            productName: null,
            oeeDefaultColorLevel: [
                {
                    lower: -Number.MAX_VALUE,
                    upper: 50,
                    color: 'negative'
                }, // negative
                {
                    lower: 50,
                    upper: 75,
                    color: 'warning'
                }, // warning
                {
                    lower: 75,
                    upper: Number.MAX_VALUE,
                    color: 'positive'
                } // positive
            ],
            noSensorConnectedMessage,
            noDataAvailableMessage
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset',
            getAssetPhase: 'facilities/getAssetPhase',
            getDeviceDisconnection: 'facilities/getDeviceDisconnection',
            isAssetConnectedToSensor: 'facilities/isAssetConnectedToSensor'
        }),
        ...mapState(useSharedStore, {
            isLoadingGlobal: 'isLoading'
        }),
        ...mapState(useProductsStore, {
            getProduct: 'getProduct'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        ...mapState(useCausesStore, {
            getCause: 'getCause'
        }),
        /**
         * Check if any part of the component is loading.
         * @return {Boolean}
         */
        showLoading() {
            return (
                this.isLoadingStats ||
                this.isLoading ||
                this.isLoadingDeviceStatus
            );
        },
        /**
         * Get asset by ID.
         * @return {Object}
         */
        asset() {
            return this.getAsset(this.assetId);
        },
        /**
         * Get asset phase.
         * @return {Object}
         */
        phase() {
            return this.getAssetPhase(this.assetId);
        },
        stats() {
            switch (this.statsPeriod) {
                case 'currentProduct':
                    return {
                        productCount:
                            'currentProductCount' in this.assetData
                                ? this.assetData['currentProductCount']
                                : null,
                        performance:
                            'currentProductPerformance' in this.assetData
                                ? this.assetData['currentProductPerformance']
                                : null,
                        availability:
                            'currentProductAvailability' in this.assetData
                                ? this.assetData['currentProductAvailability']
                                : null
                    };
                case 'currentShift':
                    return {
                        productCount:
                            'currentShiftCount' in this.assetData
                                ? this.assetData['currentShiftCount']
                                : null,
                        performance:
                            'currentShiftPerformance' in this.assetData
                                ? this.assetData['currentShiftPerformance']
                                : null,
                        availability:
                            'currentShiftAvailability' in this.assetData
                                ? this.assetData['currentShiftAvailability']
                                : null
                    };
                case 'today':
                    return {
                        productCount:
                            'todayCount' in this.assetData
                                ? this.assetData['todayCount']
                                : null,
                        performance:
                            'todayPerformance' in this.assetData
                                ? this.assetData['todayPerformance']
                                : null,
                        availability:
                            'todayAvailability' in this.assetData
                                ? this.assetData['todayAvailability']
                                : null
                    };
                default:
                    return {
                        productCount: null,
                        performance: null,
                        availability: null
                    };
            }
        },
        /**
         * Get cause id associated with phase, if there is one.
         * @return {(Number|null)}
         */
        causeId() {
            if (this.phase) {
                return this.phase.cause;
            } else {
                return null;
            }
        },
        /**
         * Get product id associated with phase, if there is one.
         * @return {(Number|null)}
         */
        productId() {
            if (this.phase) {
                return this.phase.currentProduct;
            } else {
                return null;
            }
        },
        /**
         * Get status of asset.
         * @return {(String|null)}
         */
        status() {
            if (this.phase) {
                return this.phase.status;
            } else {
                return null;
            }
        },
        /**
         * Get whether to show stats or not.
         * @return {Boolean}
         */
        isRunningOrInDowntime() {
            return (
                this.status === 'running' ||
                this.status === 'in_downtime' ||
                this.disconnected
            );
        },
        /**
         * Get the current status of the device(s)
         * to which this asset is connected to
         * @return {Boolean} - True if asset is affected
         * by any disconnected gateway else False.
         */
        disconnected() {
            return this.getDeviceDisconnection(this.assetId) ? true : false;
        },
        /**
         * Get name to display in the body of card (i.e. name of product or name of cause)
         * @return {String}
         */
        displayName() {
            if (this.disconnected) return '-';

            if (this.status === 'running' && this.productName) {
                return this.productName;
            }

            if (
                ['changeover', 'in_downtime', 'stopped'].includes(
                    this.status
                ) &&
                this.causeName
            ) {
                return this.causeName;
            }

            return '-';
        },
        /**
         * Get the total duration for which the asset has been disconnected.
         * @return {String} - Duration in human readable format 99h 99min
         */
        getDisconnectedDuration() {
            const asset = this.getDeviceDisconnection(this.assetId);

            // format disconnect duration string to 99h 99min
            if (asset && asset.start) {
                // calculate difference in milliseconds
                const diff = new Date() - new Date(asset.start);
                return this.getFormattedDuration(diff / 1000);
            }
            return '';
        },
        /**
         * Get message according to the state of the asset.
         * @return {String}
         */
        cardMessage() {
            if (!this.isLoadingDeviceStatus && !this.hasSensor) {
                return this.$t(noSensorConnectedMessage);
            } else {
                return this.$t(noDataAvailableMessage);
            }
        },
        /**
         * Check if card message should be displayed.
         * @return {Boolean}
         */
        showCardMessage() {
            // Do not show the card message if the page is loading
            // unless the asset is disconnected
            if (this.isLoadingGlobal && !this.disconnected) {
                return false;
            }
            return (
                !this.isRunningOrInDowntime ||
                this.disconnected ||
                !this.hasSensor
            );
        },
        /**
         * Check if asset has a sensor connected to it.
         * @return {Boolean} - True if asset has a sensor connected to it.
         */
        hasSensor() {
            return this.isAssetConnectedToSensor(this.assetId);
        }
    },
    methods: {
        ...mapActions(useProductsStore, ['loadProductAction']),
        ...mapActions(useCausesStore, ['loadCauseAction']),
        /**
         * Returns total of disconnected duration formatted.
         * @return {String} - 99h 59min / 59min 15sec / 15sec
         */
        getFormattedDuration(totalSeconds) {
            if (totalSeconds < 60) {
                return `${Math.round(totalSeconds)} seconds`;
            }
            const totalMinutes = Math.floor(totalSeconds / 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = Math.round(totalMinutes % 60);
            const seconds = Math.round(totalSeconds % 60);

            if (hours > 0) {
                return this.$t('{hours}h {minutes}min', {
                    hours: hours,
                    minutes: minutes
                });
            } else {
                return this.$t('{minutes}min {seconds}sec', {
                    minutes: minutes,
                    seconds: seconds
                });
            }
        },
        /**
         * Get the oee widget color
         * @param {Number} value - Value to check
         * @return {String} - Color
         */
        oeeWidgetColor(value) {
            if (!isNaN(value)) {
                for (const level of this.oeeDefaultColorLevel) {
                    const lower = level['lower'];
                    const upper = level['upper'];
                    if (value >= lower && value <= upper) {
                        return level['color'];
                    }
                }
            }
            return getCssVar('primary');
        },
        /**
         * Formats number based on data type defined in widget model.
         * @param {Number} number
         * @param {String} dataType
         * @return {String} Formatted number based on data type in widget model.
         */
        formatNumber(number, dataType) {
            switch (dataType) {
                case 'integer':
                    return number.toLocaleString(this.userLocale);
                case 'float':
                    return number.toLocaleString(this.userLocale, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 1
                    });
                default:
                    return number;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.asset-card {
    cursor: pointer;
}

.asset-card__status {
    font-weight: 900;
}

.asset-card__asset {
    font-weight: 500;
}

.asset_card__bottom-section {
    height: 50px;
}

.asset-card__bottom-section-text--ok {
    font-size: 16px;
    font-weight: 900;
}

.asset-card__bottom-section-text--issue {
    font-size: 24px;
    font-weight: 900;
}

.asset-card__stats_text {
    display: inline-block;
}

.asset-card__stats_value {
    float: right;
    font-size: larger;
}

.asset-card__stats_separator {
    margin: 15px 0px 8px 0px;
}

.asset-card__top-section-product {
    // same height as the top-section to vertically align!
    line-height: 78px;
}
.asset-card__content {
    font-size: 16px;
    min-height: 50px;
}

.asset-card__truncate {
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    overflow: hidden !important;
}

.disconnection_banner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    min-height: 40px;
    max-height: 40px;
}

.q-skeleton {
    cursor: pointer !important;
}
</style>
